import envConfig from '../../static/config';
import config from '../config';
import InternetBankingActions from '../actions/InternetBankingActions';
import InternetBankingServerActionCreator from '../actions/InternetBankingServerActionCreator';
import ApiUtils from './ApiUtils';
import MappingUtils from './MappingUtils';
import RequestOptionsBuilder from '../api/RequestOptionsBuilder';

const _getOffersEndpoint =
  '/banks/{bank-id}/cases/{case-type}/{case-subtype}/{case-id}/tasks/offers';
const _acceptOffersEndpoint =
  '/banks/{bank-id}/cases/{case-type}/{case-subtype}/{case-id}/tasks/offers/actions/decision';
const _getNextTaskEndpoint =
  '/banks/{bank-id}/cases/{case-type}/{case-subtype}/{case-id}/tasks/next';
const _submitSwitchEndpoint = '/banks/{bank-id}/cases/{case-type}';

const InternetBankingApiUtils = {
  /**
   * Send the user's form data.
   * Will PUT if we have a case ID already, or POST if not.
   *
   * @param  {object} options	Contains callbacks and object necessary for the call.
   */
  sendFormData(options, requestData) {
    const {
      callback,
      errorCallBack,
      formComplete,
      caseId,
      product,
      addAuthToken,
    } = options;

    const { url, method } = RequestOptionsBuilder.saveCase(
      product.productType,
      caseId
    );

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        addAuthToken,
        method,
        url,
        requestData,
      },
      (body) => {
        const caseId = body['reference_id'];

        if (formComplete) {
          this.postFormSubmit({ ...options, caseId });
        } else if (callback) {
          callback(caseId);
        }
      },
      (body, status) => {
        if (errorCallBack && status === 409) {
          errorCallBack(body.error.code, body.message);
        } else if (caseId) {
          // Fail silently. Allow user to continue. DYB-15245
          InternetBankingServerActionCreator.handleCaseUpdateError(body);
        } else {
          InternetBankingActions.navigateToWebTask('WEB-ERROR');
        }
      }
    );
  },

  /**
   * Submit application
   * @param  {Object} options Contains callbacks and object necessary for the call.
   * @return {void}
   */
  postFormSubmit(options) {
    const { errorCallBack, caseId, product } = options;

    const { url, method } = RequestOptionsBuilder.submitCase(
      product.productType,
      caseId
    );

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        method,
        addAuthToken: true,
        requestData: {},
        url,
      },
      () => {
        if (options.callback) {
          options.callback(options.caseId);
        }
      },
      (body, status) => {
        if (errorCallBack && status === 409) {
          errorCallBack(body.error.code, body.message);
        } else {
          InternetBankingActions.navigateToWebTask('WEB-ERROR');
        }
      }
    );
  },

  /**
   * Call the gateway services api and look up the offer for a case id
   *
   * @param {String} caseId 			The caseId for the application.
   */
  requestProductOffer(caseId) {
    const url =
      envConfig.apiBaseUrl +
      _getOffersEndpoint
        .replace('{case-type}', 'csap')
        .replace('{case-id}', caseId);

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        method: 'GET',
        url,
        addAuthToken: true,
      },
      (body) => {
        InternetBankingServerActionCreator.handleProductOfferSuccess(body);
      }
    );
  },

  /**
   * Accept a product offer.
   *
   * @param  {String} offerId 		Offer to accept.
   */
  respondToProductOffer(caseId, offerId, isDecline) {
    const url =
      envConfig.apiBaseUrl +
      _acceptOffersEndpoint
        .replace('{case-id}', caseId)
        .replace('{case-type}', 'csap');

    const offerResponse = {
      decision: isDecline ? 'REJECT' : 'ACCEPT',
    };
    offerResponse['offer_id'] = offerId;

    const requestData = {
      offers: [offerResponse],
    };

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        method: 'PUT',
        url,
        addAuthToken: true,
        requestData,
      },
      (body) => {
        // Store the Case ID.
        InternetBankingActions.updateFormValue('caseId', body['reference_id']);
      }
    );
  },

  /**
   * API call to discover what task the current case is on.
   *
   * @param  {Function} callback 		Triggered and passed the taskID on completion.
   * @param {String} caseId 			The caseId for the application.
   */
  getNextTask(callback, caseId) {
    const url =
      envConfig.apiBaseUrl +
      _getNextTaskEndpoint
        .replace('{case-type}', 'csap')
        .replace('{case-id}', caseId);

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        method: 'GET',
        url,
        addAuthToken: true,
      },
      (body) => {
        if (callback) {
          callback(body['task_uri']);
        }
      }
    );
  },

  /**
   * Submit a CAs request.
   *
   * @param  {Function} callback 	Triggered and passed status code on completion.
   */
  submitSwitchingApplication(callback, data) {
    const requestData = MappingUtils.mapSwitchPost(data);

    const url =
      envConfig.apiBaseUrl +
      _submitSwitchEndpoint.replace('{case-type}', 'cas');

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.casCaseServices,
        method: 'POST',
        url,
        addAuthToken: true,
        requestData,
      },
      () => {
        if (callback) {
          callback();
        }
      },
      (body, status) => {
        if (callback && status === 409) {
          callback(body.error.code);
        } else {
          InternetBankingActions.navigateToWebTask('WEB-ERROR');
        }
      }
    );
  },

  /**
   * Request the application data for the given case.
   *
   * @param  {String} caseId 		ID of the case.
   */
  getCase(caseId, productType) {
    const { url, method } = RequestOptionsBuilder.getCase(productType, caseId);

    ApiUtils.makeAjaxCall(
      {
        apiVersion: config.apiVersions.csapCaseServices,
        method,
        url,
        addAuthToken: true,
      },
      (body) => {
        InternetBankingActions.receiveGetResult(body);
      }
    );
  },
};

export default InternetBankingApiUtils;
